import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import TableCompo from "../Table/TableCompo";

const emptyForm = {
  name: "",
  cashfreeVendorId: "",
  splitType: "percentage",
  booking_percentage: "",
  parent_id: "",
  isActive: true,
};

const parentRef = (vendor) => vendor?.parent_id?._id || vendor?.parent_id || "";

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadVendors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/vendor`,
      );
      setVendors(response.data?.data?.vendors || []);
      setSummary(response.data?.data?.summary || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load vendors.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const parentOptions = useMemo(() => {
    return vendors.filter((vendor) => {
      if (parentRef(vendor)) return false;
      if (editing && String(vendor._id) === String(editing._id)) return false;
      return true;
    });
  }, [vendors, editing]);

  const editingHasChildren = useMemo(() => {
    if (!editing) return false;
    return vendors.some(
      (vendor) => String(parentRef(vendor)) === String(editing._id),
    );
  }, [vendors, editing]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (vendor) => {
    setEditing(vendor);
    setForm({
      name: vendor.name || "",
      cashfreeVendorId: vendor.cashfreeVendorId || "",
      splitType: vendor.splitType === "amount" ? "amount" : "percentage",
      booking_percentage: vendor.booking_percentage ?? "",
      parent_id: parentRef(vendor),
      isActive: vendor.isActive !== false,
    });
    setOpen(true);
  };

  const saveVendor = async () => {
    const payload = {
      name: form.name.trim(),
      cashfreeVendorId: form.cashfreeVendorId.trim(),
      splitType: form.splitType,
      booking_percentage: Number(form.booking_percentage),
      parent_id: form.parent_id || null,
      isActive: form.isActive,
    };
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/vendor/${editing._id}`,
          payload,
        );
        toast.success("Vendor updated.");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/vendor`,
          payload,
        );
        toast.success("Vendor saved.");
      }
      setOpen(false);
      await loadVendors();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(Array.isArray(message) ? message.join(", ") : message || "Could not save vendor.");
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "name", headerName: "Vendor" },
      { field: "cashfreeVendorId", headerName: "Cashfree vendor id" },
      {
        field: "splitType",
        headerName: "Split type",
        render: (value) => (value === "amount" ? "Amount" : "Percentage"),
      },
      {
        field: "parent_id",
        headerName: "Parent",
        render: (value) => value?.name || "Top level",
      },
      {
        field: "booking_percentage",
        headerName: "Booking %",
        render: (value, row) => {
          const parent = row.parent_id;
          if (parent?.booking_percentage) {
            const orderShare =
              (Number(parent.booking_percentage) * Number(value || 0)) / 100;
            return `${value}% of parent (${Number(orderShare.toFixed(2))}% of order)`;
          }
          return row.splitType === "amount"
            ? `${value}% of live rate`
            : `${value}% of order`;
        },
      },
      {
        field: "isActive",
        headerName: "Status",
        render: (value) => (
          <Chip
            size="small"
            label={value === false ? "Inactive" : "Active"}
            color={value === false ? "default" : "success"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Action",
        render: (_value, row) => (
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography fontWeight={600} fontSize={20}>
            Payment vendors
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {summary?.splitEnabled && summary.splitType === "amount"
              ? "Easy Split is on. Vendors receive the live rate with GST. The bullion margin, including GST on that margin, stays with the jeweller."
              : summary?.splitEnabled
                ? `Easy Split is on. ${summary.topLevelTotal}% of each payment is sent to the vendors below. The remaining ${summary.jewellerShare}% settles to the jeweller Cashfree account.`
                : "Easy Split is off until an active vendor is saved."}
          </Typography>
          {summary?.splitEnabled && summary.splitType === "amount" ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Example: a live rate of ₹15000 with 3% GST is ₹15450 for the vendor.
              A fixed margin of ₹500, with GST, stays with the jeweller.
            </Typography>
          ) : summary?.splitEnabled ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Example: a customer payment of ₹1000 sends ₹
              {((1000 * Number(summary.topLevelTotal || 0)) / 100).toFixed(2)} to
              vendors and ₹
              {((1000 * Number(summary.jewellerShare || 0)) / 100).toFixed(2)} to
              the jeweller.
            </Typography>
          ) : null}
        </Box>
        <Button variant="contained" onClick={openCreate}>
          Add vendor
        </Button>
      </Stack>

      <TableCompo
        columns={columns}
        data={vendors}
        loading={loading}
        page={0}
        limit={Math.max(vendors.length, 10)}
        totalCount={vendors.length}
      />

      {/* margin_split_percentage stays at 100 on the server until that screen is enabled */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit vendor" : "Add vendor"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Vendor name"
              value={form.name}
              onChange={(event) =>
                setForm((current) => ({ ...current, name: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Cashfree vendor id"
              value={form.cashfreeVendorId}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  cashfreeVendorId: event.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              select
              label="Split type"
              value={form.splitType}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  splitType: event.target.value,
                }))
              }
              helperText={
                form.splitType === "amount"
                  ? "The vendor receives the live rate with GST. The jeweller keeps the bullion margin, including GST on that margin."
                  : "The vendor receives this percentage of the customer payment. The rest settles to the jeweller."
              }
              fullWidth
            >
              <MenuItem value="percentage">Percentage</MenuItem>
              <MenuItem value="amount">Amount</MenuItem>
            </TextField>
            <TextField
              label="Booking percentage"
              type="number"
              value={form.booking_percentage}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  booking_percentage: event.target.value,
                }))
              }
              helperText={
                form.splitType === "amount"
                  ? "Percentage of the live rate with GST. Enter 100 to send the full GST-inclusive live rate to this vendor. The bullion margin stays with the jeweller."
                  : form.parent_id
                    ? "Percentage of the parent vendor's share. Children of one parent cannot total more than 100%."
                    : "Percentage of the customer payment sent to this vendor. For 10% of ₹1000, enter 10. All top-level vendors together cannot exceed 100%."
              }
              fullWidth
            />
            <TextField
              select
              label="Parent vendor"
              value={form.parent_id}
              disabled={editingHasChildren}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  parent_id: event.target.value,
                }))
              }
              helperText={
                editingHasChildren
                  ? "This vendor has children, so it stays a top-level vendor."
                  : "Leave empty for a top-level vendor. Cashfree is paid the child vendors when a parent has children."
              }
              fullWidth
            >
              <MenuItem value="">Top level</MenuItem>
              {parentOptions.map((vendor) => (
                <MenuItem key={vendor._id} value={vendor._id}>
                  {vendor.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={form.isActive ? "active" : "inactive"}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  isActive: event.target.value === "active",
                }))
              }
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveVendor} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
